'use client';

import { useEffect, useRef } from 'react';
import type { Restaurant } from '@/app/types/restaurant';
import { RestaurantCard } from '@/app/components/restaurant-card';
import { SkeletonCard } from '@/app/components/skeleton-card';

type RestaurantListProps = {
  restaurants: Restaurant[];
  selectedId: number | null;
  onCardClick: (restaurant: Restaurant) => void;
  isLoading?: boolean;
  avgRatings?: Record<number, { avg: number; count: number }>;
};

export function RestaurantList({ restaurants, selectedId, onCardClick, isLoading, avgRatings }: RestaurantListProps) {
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    if (selectedId != null && cardRefs.current[selectedId]) {
      cardRefs.current[selectedId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedId]);

  if (isLoading) {
    return (
      <div className="space-y-2 md:space-y-3 p-3 md:p-4">
        {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  if (restaurants.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-4xl mb-3">😕</p>
        <p className="font-display font-medium text-gray-600">Không tìm thấy quán nào</p>
        <p className="text-sm text-gray-500 mt-1">Thử thay đổi bộ lọc hoặc tìm kiếm khác</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 md:space-y-3 p-3 md:p-4">
      {restaurants.map((r) => {
        const ratingData = avgRatings?.[r.stt];
        return (
          <div
            key={r.stt}
            ref={(el) => { cardRefs.current[r.stt] = el; }}
            onClick={() => onCardClick(r)}
            className="cursor-pointer"
          >
            <RestaurantCard
              restaurant={r}
              isSelected={r.stt === selectedId}
              avgRating={ratingData?.avg}
              reviewCount={ratingData?.count}
            />
          </div>
        );
      })}
    </div>
  );
}
